import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { FaApple, FaFacebookF } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useToast } from "@chakra-ui/react";

export default function Signup() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const { user, authLoading, loginWithGoogle } = useAuth();

  // If already logged in, redirect to home
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Show toast if redirected here with an error
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get("error");

    if (error === "already_registered") {
      toast({
        title: "Account already exists",
        description: "You already have an account. Please sign in instead.",
        status: "warning",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }

    if (error === "server_error") {
      toast({
        title: "Something went wrong",
        description: "Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  }, [location.search, toast]);

  if (authLoading) return null;

  return (
    <Box
      minH="100vh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      px="clamp(20px, 4vw, 48px)"
      py="clamp(24px, 4vh, 48px)"

      boxSizing='border-box'
      transition="margin-left 0.25s ease"
      border="1px solid #ECECEC"
      borderRadius="20px"
      bgGradient="linear(to-b, #EAFFFF 0%, white 30%, white 60%, #EAFFFF 100%)"
    >
      <VStack
        w="100%"
        maxW="clamp(320px, 90vw, 420px)"
        spacing="clamp(20px, 2.5vw, 28px)"
        align="stretch"
      >
        {/* Logo */}
        <Flex justify="center" mb="clamp(8px, 1vh, 16px)">
          <Link as={RouterLink} to="/">
            <Image
              src="/logoSignUp.svg"
              alt="Alpherra"
              // h="clamp(28px, 2.2vw, 40px)"
              h="clamp(48px, 4vw, 72px)"
              objectFit="contain"
            />
          </Link>
        </Flex>

        {/* Heading */}
        <Text
          fontSize="clamp(24px, 2.2vw, 32px)"
          fontWeight="semibold"
          color="#242424"
          textAlign="center"
          lineHeight="1.25"
        >
          Create an account
        </Text>

        {/* Social signup buttons */}
        <VStack spacing="clamp(10px, 1vw, 14px)" w="100%" align="stretch">
          <Button
            type="button"
            w="100%"
            h="clamp(44px, 4vh, 52px)"
            borderRadius="12px"
            border="1px solid #E0E0E0"
            bg="white"
            color="#242424"
            fontSize="clamp(14px, 1vw, 16px)"
            fontWeight="medium"
            _hover={{ bg: "#F8F8F8", borderColor: "#D0D0D0" }}
            leftIcon={
              <Box
                as="span"
                display="inline-block"
                w="clamp(18px, 1.3vw, 22px)"
                h="clamp(18px, 1.3vw, 22px)"
              >
                <svg viewBox="0 0 48 48" width="100%" height="100%">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.73 1.22 9.24 3.61l6.9-6.9C35.64 2.54 30.2 0 24 0 14.64 0 6.48 5.38 2.56 13.22l8.06 6.26C12.5 13.38 17.74 9.5 24 9.5z" />
                  <path fill="#4285F4" d="M46.1 24.55c0-1.64-.15-3.22-.42-4.73H24v9.01h12.4c-.54 2.91-2.18 5.37-4.66 7.02l7.18 5.58C43.96 37.4 46.1 31.51 46.1 24.55z" />
                  <path fill="#FBBC05" d="M10.62 28.48A14.5 14.5 0 0 1 9.5 24c0-1.56.27-3.07.76-4.48l-8.06-6.26A23.98 23.98 0 0 0 0 24c0 3.77.9 7.34 2.2 10.26l8.42-5.78z" />
                  <path fill="#34A853" d="M24 48c6.2 0 11.4-2.05 15.2-5.57l-7.18-5.58c-2 1.34-4.57 2.13-8.02 2.13-6.26 0-11.5-3.88-13.38-9.48l-8.42 5.78C6.48 42.62 14.64 48 24 48z" />
                </svg>
              </Box>
            }
            onClick={() => loginWithGoogle('signup')}
          >
            Sign up with Google
          </Button>
          <Button
            type="button"
            w="100%"
            h="clamp(44px, 4vh, 52px)"
            borderRadius="12px"
            border="1px solid #E0E0E0"
            bg="white"
            color="#242424"
            fontSize="clamp(14px, 1vw, 16px)"
            fontWeight="medium"
            _hover={{ bg: "#F8F8F8", borderColor: "#D0D0D0" }}
            leftIcon={<Box as={FaApple} fontSize="clamp(18px, 1.3vw, 22px)" />}
          >
            Sign up with Apple
          </Button>
          <Button
            type="button"
            w="100%"
            h="clamp(44px, 4vh, 52px)"
            borderRadius="12px"
            border="1px solid #E0E0E0"
            bg="white"
            color="#242424"
            fontSize="clamp(14px, 1vw, 16px)"
            fontWeight="medium"
            _hover={{ bg: "#F8F8F8", borderColor: "#D0D0D0" }}
            leftIcon={<Box as={FaFacebookF} color="#1877F2" fontSize="clamp(18px, 1.3vw, 22px)" />}
          >
            Sign up with Facebook
          </Button>
        </VStack>

        {/* OR separator */}
        <Flex align="center" w="100%" gap="clamp(12px, 1.5vw, 20px)">
          <Box flex={1} h="1px" bg="#E0E0E0" />
          <Text fontSize="clamp(13px, 1vw, 15px)" color="#6D6D6D" fontWeight="medium">
            OR
          </Text>
          <Box flex={1} h="1px" bg="#E0E0E0" />
        </Flex>

        {/* Form */}
        <VStack spacing="clamp(16px, 1.5vw, 20px)" align="stretch" as="form">
          <FormControl isRequired>
            <FormLabel
              fontSize="clamp(14px, 1vw, 16px)"
              fontWeight="medium"
              color="#242424"
              mb="clamp(6px, 0.5vw, 8px)"
              sx={{
                '& .chakra-form__required-symbol': {
                  display: 'none !important'  // Hides the asterisk pseudo-element
                }
              }}
            >
              Full name
            </FormLabel>
            <Input
              placeholder="Enter full name"
              size="lg"
              h="clamp(44px, 4vh, 52px)"
              borderRadius="12px"
              border="1px solid #ECECEC"
              _placeholder={{ color: "#9E9E9E" }}
              _hover={{ borderColor: "#D0D0D0" }}
              _focus={{ borderColor: "#CF008A", boxShadow: "0 0 0 1px #CF008A" }}
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel
              fontSize="clamp(14px, 1vw, 16px)"
              fontWeight="medium"
              color="#242424"
              mb="clamp(6px, 0.5vw, 8px)"
            >
              Email address
            </FormLabel>
            <Input
              type="email"
              placeholder="Enter email address"
              size="lg"
              h="clamp(44px, 4vh, 52px)"
              borderRadius="12px"
              border="1px solid #ECECEC"
              _placeholder={{ color: "#9E9E9E" }}
              _hover={{ borderColor: "#D0D0D0" }}
              _focus={{ borderColor: "#CF008A", boxShadow: "0 0 0 1px #CF008A" }}
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel
              fontSize="clamp(14px, 1vw, 16px)"
              fontWeight="medium"
              color="#242424"
              mb="clamp(6px, 0.5vw, 8px)"
            >
              Create password
            </FormLabel>
            <InputGroup size="lg">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                h="clamp(44px, 4vh, 52px)"
                borderRadius="12px"
                border="1px solid #ECECEC"
                _placeholder={{ color: "#9E9E9E" }}
                _hover={{ borderColor: "#D0D0D0" }}
                _focus={{ borderColor: "#CF008A", boxShadow: "0 0 0 1px #CF008A" }}
              />
              <InputRightElement h="clamp(44px, 4vh, 52px)">
                <Button
                  variant="ghost"
                  size="sm"
                  fontSize="clamp(12px, 0.9vw, 14px)"
                  color="#6D6D6D"
                  _hover={{ bg: "#F5F5F5" }}
                  onClick={() => setShowPassword((p) => !p)}
                >
                  {showPassword ? "Hide" : "Show"}
                </Button>
              </InputRightElement>
            </InputGroup>
          </FormControl>

          <FormControl>
            <Checkbox
              colorScheme="pink"
              size="md"
              color="#6D6D6D"
              fontSize="clamp(13px, 1vw, 15px)"
              sx={{ "& .chakra-checkbox__control": { borderColor: "#C0C0C0" } }}
              defaultChecked
            >
              Remember me for 30 days
            </Checkbox>
          </FormControl>

          <VStack spacing={0} align="stretch" w="100%">
            <Button
              type="submit"
              bg="#CF008A"
              color="white"
              size="lg"
              h="clamp(48px, 4.5vh, 56px)"
              fontSize="clamp(15px, 1.1vw, 17px)"
              fontWeight="semibold"
              borderRadius="12px"
              _hover={{ bg: "#D933A1" }}
              _active={{ bg: "#B8007A" }}
              mb="clamp(4px, 0.4vw, 6px)"
            >
              Sign up
            </Button>

            <Text
              fontSize="clamp(12px, 0.9vw, 14px)"
              color="#6D6D6D"
              textAlign="center"
              lineHeight="1.5"
            >
              By signing up, I agree to our{" "}
              <Link color="#CF008A" href="#" textDecoration="underline" _hover={{ color: "#D933A1" }}>
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link color="#CF008A" href="#" textDecoration="underline" _hover={{ color: "#D933A1" }}>
                Privacy Policy
              </Link>
              .
            </Text>
          </VStack>


        </VStack>

        {/* Sign in link */}
        <Flex
          align="center"
          justify="center"
          gap="8px"
        >
          <Text
            fontSize="clamp(16px, 1vw, 18px)"
            color="#6D6D6D"
          >
            Already have an account?
          </Text>
          <Link
            as={RouterLink}
            to="/signin"
            fontSize="clamp(16px, 1vw, 18px)"
            color="#CF008A"
          >
            Sign in
          </Link>
        </Flex>
      </VStack>
    </Box>
  );
}
